import styled from '@emotion/styled'

/*
 * One icon in a collapsed icon rail: a circular hit target that fills light blue
 * on hover — and stays filled while it is the tab the user is on — plus a label
 * that appears to its right. The rail is icon-only, so the label is the only
 * thing naming a destination: it reveals on keyboard focus as well as on hover,
 * or tabbing through the rail would be a walk through unlabeled circles.
 *
 * Shared by the collapsed main sidebar and the Hey Nic chat history rail so the
 * two rails hover and read the same way.
 *
 * The rail that hosts these needs `overflow: visible` (and to sit above the
 * content beside it) so the labels can escape its width.
 */

// The on-hover circle is theme.ternaryColor. The active circle has no design
// token, so Zeplin's raw value stands in.
// TODO: Use design token for color rgba(217, 238, 244, 1)
const ACTIVE_CIRCLE_COLOR = '#d9eef4'

const LABEL_CLASS = 'rail-icon-label'

const Cell = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Pinning the cell to a fixed width keeps the icon in its spot while a rail
     that animates its width is still mid-transition, instead of letting it ride
     the rail's left edge. */
  ${p =>
    p.cellWidth &&
    `flex: 0 0 auto; width: ${p.cellWidth}; align-self: flex-start; box-sizing: border-box;`}

  & > .${LABEL_CLASS} {
    position: absolute;
    left: calc(100% + 8px);
    top: 50%;
    transform: translateY(-50%);
    white-space: nowrap;
    background: ${p => p.theme.bodyTextColor ?? '#31424a'};
    color: #ffffff;
    font-size: 12px;
    padding: 5px 9px;
    border-radius: 6px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s;
    z-index: 2;
  }
  &:hover > .${LABEL_CLASS}, & > button:focus-visible + .${LABEL_CLASS} {
    opacity: 1;
  }
`

const Circle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${p => (p.isActive ? ACTIVE_CIRCLE_COLOR : 'transparent')};
  cursor: pointer;
  transition: background 0.2s;
  & svg {
    display: block;
    width: 30px;
    height: 30px;
  }
  &:hover,
  &:focus-visible {
    background: ${p => (p.isActive ? ACTIVE_CIRCLE_COLOR : (p.theme.ternaryColor ?? '#e7f3f9'))};
  }
  &:focus-visible {
    outline: 2px solid ${p => p.theme.primaryColor ?? '#038ab7'};
    outline-offset: 2px;
  }
  &:disabled {
    cursor: default;
    opacity: 0.4;
    background: transparent;
  }
`

const RailIcon = ({
  label,
  isActive = false,
  disabled = false,
  onClick,
  cellWidth,
  className,
  children,
}) => (
  <Cell cellWidth={cellWidth} className={className}>
    <Circle
      type='button'
      isActive={isActive}
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      aria-current={isActive ? 'page' : undefined}>
      {children}
    </Circle>
    <span className={LABEL_CLASS}>{label}</span>
  </Cell>
)

export default RailIcon
